// src/pages/Dashboard.jsx
import React, { useState, useMemo } from "react";
import { fmt, PRICES } from "../config/devices";
import { StatCard, HBar, YearPill, FilterBtn, SectionTitle, Card } from "../components/UI";

export default function Dashboard({ computeDevices, allDevices, csvFiles, jiraMap, handleCSV, priceMap, jiraLoading, jiraError, jiraLoadedAt, defenderLoading, devicePrice, lwtLoadedAt, lwtFallback, lifecycleYears = 4 }) {
  const prices = priceMap || PRICES;
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear() + 1);
  const [campusFilter, setCampusFilter] = useState("All");
  const [dragging, setDragging] = useState(false);
  const currentYear = new Date().getFullYear();
  const noData = (allDevices || []).length === 0;

  const campuses = useMemo(() => ["All", ...Array.from(new Set(computeDevices.map(d => d.campus))).sort()], [computeDevices]);

  const filtered = useMemo(() =>
    computeDevices.filter(d => campusFilter === "All" || d.campus === campusFilter),
    [computeDevices, campusFilter]
  );

  const years = useMemo(() => {
    const min = currentYear + 1;
    const max = lifecycleYears === 5 ? currentYear + 5 : currentYear + 4;
    const all = []; for (let y = min; y <= max; y++) all.push(y); return all;
  }, [filtered, currentYear, lifecycleYears]);
  const yearMap = useMemo(() => {
    const m = {};
    years.forEach(y => { m[y] = filtered.filter(d => d.eolYear === y); });
    return m;
  }, [filtered, years]);

  const maxCount = useMemo(() => Math.max(...Object.values(yearMap).map(d => d.length), 1), [yearMap]);
  const yearDevices = useMemo(() => yearMap[selectedYear] || [], [yearMap, selectedYear]);
  const yearBudget = yearDevices.reduce((s, d) => s + (prices[d.deviceType] || 0), 0);

  const byType = useMemo(() => {
    const m = {};
    yearDevices.forEach(d => { m[d.deviceType] = (m[d.deviceType] || 0) + 1; });
    return Object.entries(m).sort((a,b)=>b[1]-a[1]);
  }, [yearDevices]);

  const byCampus = useMemo(() => {
    const m = {};
    yearDevices.forEach(d => { m[d.campus] = (m[d.campus] || 0) + 1; });
    return Object.entries(m).sort((a,b)=>b[1]-a[1]);
  }, [yearDevices]);

  const jiraAssigned = yearDevices.filter(d => d.assignedTo).length;

  if (noData) {
    return (
      <div className="fade-up">
        <div style={{ marginBottom: 32 }}>
          <h1 style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 28, fontWeight: 800, letterSpacing: "-0.03em", color: "var(--text)", marginBottom: 6 }}>{lifecycleYears}-Year Device Lifecycle</h1>
          <p style={{ color: "var(--muted)", fontSize: 14 }}>Load your LWT CSV exports to get started. Data is saved locally — upload once.</p>
        </div>
        <div
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={e => { e.preventDefault(); setDragging(false); const f = e.dataTransfer.files[0]; if (f) handleCSV(f); }}
          style={{ border: `2px dashed ${dragging ? "var(--accent)" : "var(--border2)"}`, borderRadius: 16, padding: "64px 40px", textAlign: "center", background: dragging ? "rgba(79,156,249,0.05)" : "var(--bg2)", transition: "all 0.2s", marginBottom: 24 }}
        >
          <div style={{ fontSize: 40, marginBottom: 16, opacity: 0.4 }}>↑</div>
          <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Drop your LWT CSV export here</div>
          <div style={{ color: "var(--muted)", fontSize: 13, marginBottom: 28, maxWidth: 440, margin: "0 auto 28px" }}>
            Reads Serial, Product Description, Owner Details, Date Ordered, Warranty Date, Order No, Invoice ID directly. Accessories and student devices auto-excluded.
          </div>
          <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
            <label style={{ background: "var(--accent)", color: "#fff", padding: "10px 26px", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
              Browse CSV <input type="file" accept=".csv" style={{ display: "none" }} onChange={e => e.target.files[0] && handleCSV(e.target.files[0])} />
            </label>
          </div>
        </div>
        <div style={{ background: "rgba(79,156,249,0.07)", border: "1px solid rgba(79,156,249,0.2)", borderRadius: 10, padding: "12px 16px", fontSize: 13, color: "var(--muted)" }}>
          💡 Data persists between sessions — you won't need to upload again unless you want to update it.
        </div>
      </div>
    );
  }

  return (
    <div className="fade-up">
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 28, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 26, fontWeight: 800, letterSpacing: "-0.03em", color: "var(--text)", marginBottom: 4 }}>{lifecycleYears}-Year Device Lifecycle</h1>
          <div style={{ fontSize: 13, color: "var(--muted)" }}>
            {lifecycleYears === 5 ? "Ratified five-year lifecycle; the completed 2026 refresh is excluded and remaining devices use LWT Date Ordered + 5 years" : "Archived four-year planning from 2027; the completed 2026 refresh is excluded"}
          </div>
          {(() => {
            const staffCount = (allDevices || []).filter(d => d.deviceType === "Staff Device").length;
            const otherCount = (allDevices || []).filter(d => d.deviceType === "Other" && d.isCompute).length;
            const loadTime = lwtLoadedAt
              ? new Date(lwtLoadedAt).toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit", hour12: true })
              : null;
            if (lwtFallback) {
              return (
                <div style={{ fontSize: 11, color: "rgba(245,158,11,0.75)", marginTop: 4 }}>
                  ⚠ Using cached data — LWT unavailable · {staffCount} staff devices
                </div>
              );
            }
            if (loadTime) {
              return (
                <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}>
                  📦 LWT data: {staffCount} staff · {otherCount} other · loaded {loadTime}
                </div>
              );
            }
            return null;
          })()}
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          {/* Sync status */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 8, padding: "7px 12px", fontSize: 12 }}>
            {jiraLoading ? (
              <><span className="spinner" style={{ width: 10, height: 10, flexShrink: 0 }} /><span style={{ color: "var(--muted)" }}>Fetching assets…</span></>
            ) : defenderLoading ? (
              <><span className="spinner" style={{ width: 10, height: 10, flexShrink: 0 }} /><span style={{ color: "var(--muted)" }}>Fetching last logins…</span></>
            ) : jiraError ? (
              <><span style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--danger)", display: "inline-block", flexShrink: 0 }} /><span style={{ color: "var(--danger)" }}>Jira sync failed</span></>
            ) : jiraLoadedAt ? (
              <><span style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--success)", display: "inline-block", flexShrink: 0 }} /><span style={{ color: "var(--success)" }}>Jira synced · {Object.keys(jiraMap).length} assets · {new Date(jiraLoadedAt).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" })}</span></>
            ) : (
              <span style={{ color: "var(--muted)" }}>Not synced</span>
            )}
          </div>

          <div style={{ background: "var(--bg2)", border: "1px solid var(--border)", color: "var(--muted)", padding: "7px 14px", borderRadius: 8, fontSize: 12, whiteSpace: "nowrap" }}>
            Cost basis: <span style={{ color: "var(--warn)", fontWeight: 700 }}>{fmt(devicePrice ?? 1704)}</span> per staff device
          </div>

        </div>
      </div>

      <Card style={{ marginBottom: 20 }}>
        <SectionTitle>Sync Rules</SectionTitle>
        <div style={{ display: "grid", gap: 6, fontSize: 12, color: "var(--muted)", lineHeight: 1.65 }}>
          <div><strong style={{ color: "var(--text)" }}>LWT</strong> loads live via the LWT API on every app startup. If LWT is unavailable, the app falls back to cached data stored in Azure Blob Storage for that session.</div>
          <div><strong style={{ color: "var(--text)" }}>Jira Assets</strong> auto-syncs after LWT loads and enriches staff devices with assignment, status, hostname and campus data.</div>
          <div><strong style={{ color: "var(--text)" }}>Azure / Defender</strong> then adds last login and last seen data for budgetary staff devices during each Jira refresh.</div>
          <div><strong style={{ color: "var(--text)" }}>Budget figures</strong> are based on staff refresh devices only. Jira status does not automatically remove a device from budget.</div>
        </div>
      </Card>

      {/* Campus filter */}
      <div style={{ display: "flex", gap: 6, marginBottom: 20, flexWrap: "wrap" }}>
        {campuses.map(c => (
          <FilterBtn key={c} label={c} active={campusFilter === c} onClick={() => setCampusFilter(c)} color={c === "CCK" ? "var(--cck)" : c === "CCW" ? "var(--ccw)" : undefined} />
        ))}
      </div>

      {/* Year strip */}
      <div style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 10, marginBottom: 24 }}>
        {years.map(y => (
          <YearPill key={y} year={y} devices={yearMap[y] || []} isSelected={selectedYear === y} onClick={() => setSelectedYear(y)} maxCount={maxCount} devicePrice={devicePrice ?? 1704} />
        ))}
      </div>

      {/* Stat cards */}
      <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
        <StatCard label="EOL Devices" value={yearDevices.length} color={yearDevices.length > 40 ? "var(--danger)" : "var(--text)"} sub={`${selectedYear} · ${campusFilter !== "All" ? campusFilter : "all campuses"}`} />
        <StatCard label="Budget Required" value={fmt(yearBudget)} sub={`@ ${fmt(devicePrice ?? 1704)} per device`} color="var(--warn)" />
        <StatCard label="CCK" value={yearDevices.filter(d=>d.campus==="CCK").length} sub="Kingston" />
        <StatCard label="CCW" value={yearDevices.filter(d=>d.campus==="CCW").length} sub="Wyndham" />
        {Object.keys(jiraMap).length > 0 && (
          <StatCard label="Jira Assigned" value={jiraAssigned} sub={`of ${yearDevices.length} have assignee`} color="var(--success)" />
        )}
      </div>

      {/* Charts */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
        <Card>
          <SectionTitle>By device type</SectionTitle>
          {byType.map(([t, c]) => (
            <HBar key={t} label={t} count={c} max={yearDevices.length} color="var(--accent)" sub={prices[t] ? fmt(c * prices[t]) : null} />
          ))}
        </Card>
        <Card>
          <SectionTitle>By campus</SectionTitle>
          {byCampus.map(([cam, c]) => (
            <HBar key={cam} label={cam} count={c} max={yearDevices.length} color={cam === "CCK" ? "var(--cck)" : cam === "CCW" ? "var(--ccw)" : "var(--muted)"} />
          ))}
        </Card>
      </div>

    </div>
  );
}
